/** @jsxImportSource @emotion/react */
import { styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { css, type CSSObject } from '@emotion/react';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { ORG_ALIAS_MAX_LENGTH } from '@kaiizen/kai-pro-api';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { CommonField, CommonFieldAdornment } from '@kaiizen/kai-shiki/react';
import {
  ORG_SETTINGS_BODY_TEXT_LIGHT_STYLE,
  ORG_SETTINGS_BODY_TEXT_STYLE,
  ORG_SETTINGS_COLORS,
  ORG_SETTINGS_PANEL_HEADER_STYLE,
  ORG_SETTINGS_PANEL_STYLE,
  ORG_SETTINGS_SECTION_INSET_STYLE,
  ORG_SETTINGS_SUBHEADING_STYLE,
} from './OrgSettingsStyleLib';

type InputsOrganizationAliasPanelProps = {
  disabled?: boolean;
  orgAlias: string;
  previewLabel: string;
  validationMessage?: string;
  onOrgAliasChange?: (value: string) => void;
  /** Autosave commits the pending alias here rather than waiting out the pause (KAIDEV-4345). */
  onOrgAliasBlur?: () => void;
  /** Marks the field as being typed in, so a refresh does not rewrite it mid-word. */
  onOrgAliasFocus?: () => void;
};

const PANEL_WRAPPER_STYLE = css`
  label: PANEL_WRAPPER_STYLE;
  ${ORG_SETTINGS_SECTION_INSET_STYLE}
`;

const PANEL_BODY_STYLE = css`
  label: PANEL_BODY_STYLE;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const DESCRIPTION_STYLE = css`
  label: DESCRIPTION_STYLE;
  ${ORG_SETTINGS_BODY_TEXT_STYLE}
`;

const FIELD_WRAPPER_STYLE = {
  position: 'relative',
  maxWidth: 360,
  minHeight: 53,
  '& input': {
    fontSize: `${pxToRem(FONT_SIZE.bodyInput)} !important`,
  },
} satisfies CSSObject;

const FIELD_STYLE = {
  width: '100%',
  background: 'transparent',
} satisfies CSSObject;

const FIELD_BOX_STYLE = {
  border: `1px solid ${ORG_SETTINGS_COLORS.textFieldLabel}`,
  borderRadius: 0,
  background: ORG_SETTINGS_COLORS.white,
} satisfies CSSObject;

const FIELD_BOX_ERROR_STYLE = {
  borderColor: 'var(--red)',
} satisfies CSSObject;

const FIELD_LABEL_STYLE = {
  background: ORG_SETTINGS_COLORS.pageBackground,
} satisfies CSSObject;

const FIELD_VALUE_STYLE = {
  fontFamily: FONT.body,
  fontWeight: FONT_WEIGHT.regular,
  minWidth: 0,
  paddingRight: 56,
} satisfies CSSObject;

const FIELD_COUNT_STYLE = {
  position: 'absolute',
  right: 20,
  top: 23,
  fontFamily: FONT.body,
  fontSize: pxToRem(FONT_SIZE.bodyMicro),
  fontStyle: 'normal',
  fontWeight: FONT_WEIGHT.regular,
  lineHeight: 1.6,
  letterSpacing: '0.2px',
  color: ORG_SETTINGS_COLORS.textMuted,
} satisfies CSSObject;

const HELPER_TEXT_STYLE = css`
  label: HELPER_TEXT_STYLE;
  ${ORG_SETTINGS_BODY_TEXT_LIGHT_STYLE}
`;

const ERROR_TEXT_STYLE = css`
  label: ERROR_TEXT_STYLE;
  ${ORG_SETTINGS_BODY_TEXT_LIGHT_STYLE}
  color: var(--red);
`;

const PREVIEW_ROW_STYLE = css`
  label: PREVIEW_ROW_STYLE;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PREVIEW_TITLE_STYLE = css`
  label: PREVIEW_TITLE_STYLE;
  ${ORG_SETTINGS_SUBHEADING_STYLE}
  font-size: ${pxToRem(FONT_SIZE.bodyLarge)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.25;
  letter-spacing: 0;
`;

const PREVIEW_VALUE_STYLE = css`
  label: PREVIEW_VALUE_STYLE;
  ${ORG_SETTINGS_BODY_TEXT_STYLE}
`;

/**
 * Alias editor for the property ID label prefix used across the org.
 */
export const InputsOrganizationAliasPanel = ({
  disabled = false,
  orgAlias,
  previewLabel,
  validationMessage,
  onOrgAliasChange,
  onOrgAliasBlur,
  onOrgAliasFocus,
}: InputsOrganizationAliasPanelProps) => (
  <div css={PANEL_WRAPPER_STYLE}>
    <div css={ORG_SETTINGS_PANEL_STYLE}>
      <div css={ORG_SETTINGS_PANEL_HEADER_STYLE}>Organization Alias</div>

      <div css={PANEL_BODY_STYLE}>
        <div css={DESCRIPTION_STYLE}>
          Set an optional alias used in property ID labels throughout Kai. Leave it blank to use automatic initials from your organization name.
        </div>

        <div css={FIELD_WRAPPER_STYLE}>
          <CommonField
            label='Alias'
            style={FIELD_STYLE}
            state={disabled ? 'disabled' : 'default'}
            boxCss={[
              FIELD_BOX_STYLE,
              validationMessage ? FIELD_BOX_ERROR_STYLE : undefined,
            ]}
            labelCss={FIELD_LABEL_STYLE}
            valueCss={FIELD_VALUE_STYLE}
            inputProps={{
              value: orgAlias,
              placeholder: 'Optional short alias',
              maxLength: ORG_ALIAS_MAX_LENGTH,
              onChange: event => onOrgAliasChange?.(event.target.value),
              onBlur: () => onOrgAliasBlur?.(),
              onFocus: () => onOrgAliasFocus?.(),
            }}
          />
          <CommonFieldAdornment css={FIELD_COUNT_STYLE}>
            {`${orgAlias.length}/${ORG_ALIAS_MAX_LENGTH}`}
          </CommonFieldAdornment>
        </div>

        <div css={validationMessage ? ERROR_TEXT_STYLE : HELPER_TEXT_STYLE}>
          {validationMessage
            ?? `Aliases can be up to ${ORG_ALIAS_MAX_LENGTH} characters. "KAI" is reserved for the product name.`}
        </div>

        <div css={PREVIEW_ROW_STYLE}>
          <div css={PREVIEW_TITLE_STYLE}>Preview</div>
          <div css={PREVIEW_VALUE_STYLE}>{previewLabel}</div>
        </div>
      </div>
    </div>
  </div>
);
